const STORAGE_KEY = "native_package_metadata";

export type PackageEntry = {
  name: string;
  version: string;
  licenses: string;
  repository: string;
  publisher: string;
};

type NativeRawPackage = {
  name: string;
  version: string;
  homepage?: string;
  repository?: string;
  license?: string;
};

type NativePackageMetadata = {
  platform: string;
  generatedAt: string;
  packages: NativeRawPackage[];
};

type WebPackageMetadata = {
  platform: string;
  generatedAt: string;
  packages: PackageEntry[];
};

declare global {
  interface Window {
    setNativePackageMetadata?: (metadata: NativePackageMetadata) => void;
  }

  interface WindowEventMap {
    nativePackageMetadataChanged: CustomEvent<NativePackageMetadata>;
  }
}

let initialized = false;

export function initNativePackageListener(): () => void {
  if (initialized) return () => {};
  initialized = true;

  const handleMetadata = (metadata: NativePackageMetadata) => {
    if (metadata && Array.isArray(metadata.packages)) {
      const mapped = metadata.packages.map(mapNativePackage);
      saveNativePackages(mapped);
      window.dispatchEvent(new Event("nativePackageMetadataUpdated"));
    }
  };

  const handleEvent = (event: CustomEvent<NativePackageMetadata>) => {
    handleMetadata(event.detail);
  };

  window.addEventListener("nativePackageMetadataChanged", handleEvent);
  window.setNativePackageMetadata = handleMetadata;

  return () => {
    initialized = false;
    window.removeEventListener("nativePackageMetadataChanged", handleEvent);
    delete window.setNativePackageMetadata;
  };
}

export function mapWebPackages(data: WebPackageMetadata): PackageEntry[] {
  return (data.packages || []).map((pkg) => ({
    name: pkg.name || "",
    version: pkg.version || "",
    licenses: pkg.licenses || "",
    repository: (pkg.repository || "").trim(),
    publisher: pkg.publisher || "",
  }));
}

function mapNativePackage(pkg: NativeRawPackage): PackageEntry {
  return {
    name: pkg.name || "",
    version: pkg.version || "",
    licenses: pkg.license || "",
    repository: (pkg.repository || pkg.homepage || "").trim(),
    publisher: "",
  };
}

function saveNativePackages(packages: PackageEntry[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(packages));
  } catch (error) {
    console.error("Failed to save native package metadata:", error);
  }
}

export function getNativePackages(): PackageEntry[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}
